import React, { useState, useMemo, useEffect, useRef } from "react";
import getAddressLatLong from "../../../shared/utils/getAddressLatLong";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import styles from "../../../styles/location.module.css";
import { CircularProgress } from "@mui/material";

import { Paper, Switch, IconButton, Button } from "@mui/material";

const mapContainerStyle = {
  width: "100%",
  height: "450px",
};

const defaultCenter = {
  lat: 20.5937, // Default latitude (center of India)
  lng: 78.9629, // Default longitude (center of India)
};

const libraries = ["places"]; // Include required libraries

const ProductStoreMapping = ({
  CampaignDetails,
  showToggles = false,
  activeProduct, // 👈 this is the only filter
  onVendorViewModeChange,
  allVendors = null, // all vendors from parent
  approvedVendors = null, // approved vendors from parent
}) => {
  const [map, setMap] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [displayAllVendors, setDisplayAllVendors] = useState([]);
  const [displayApprovedVendors, setDisplayApprovedVendors] = useState([]);
  const [showVendors, setShowVendors] = useState(true);
  const [vendorViewMode, setVendorViewMode] = useState('approved');
  const [ipList, setIPList] = useState([]);
  const [showIPs, setShowIPs] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  const uniqueLocations = useMemo(() => {
    if (!CampaignDetails?.locations) return [];
    return [...CampaignDetails.locations];
  }, [CampaignDetails?.locations]);

  const uniqueSites = useMemo(() => {
    if (!CampaignDetails?.locations) return [];
    const storeMap = {};

    CampaignDetails.locations.forEach((location) => {
      const storeKey = `${location?.details?.storeName}-${location?.details?.storeOwnerPhoneNumber}`;

      if (!storeMap[storeKey]) {
        storeMap[storeKey] = {
          details: location.details,
          locationCoordinates: location.locationCoordinates,
          products: [],
        };
      }

      if (location.product) {
        storeMap[storeKey].products.push(location.product);
      }
    });

    return Object.values(storeMap);
  }, [CampaignDetails?.locations]);

  // Filter stores based on activeProduct
  const filteredUniqueSites = useMemo(() => {
    if (!uniqueSites) return [];
    if (!activeProduct) return uniqueSites;

    return uniqueSites?.filter((store) =>
      store.products?.some((p) => p?.productType === activeProduct),
    );
  }, [uniqueSites, activeProduct]);

  // 👇 Filter directly using activeProduct
  const filteredLocations = useMemo(() => {
    if (!uniqueLocations) return [];
    if (!activeProduct) return uniqueLocations; // if no active product, show all

    return uniqueLocations.filter(
      (loc) => loc?.product?.productType === activeProduct,
    );
  }, [uniqueLocations, activeProduct]);

  // Use approved vendors from parent
  useEffect(() => {
    if (approvedVendors && approvedVendors.length > 0) {
      setDisplayApprovedVendors(approvedVendors);
    }
  }, [approvedVendors]);

  // Update map center when filtered locations change
  useEffect(() => {
    if (filteredLocations?.length > 0) {
      const pos = getPosition(filteredLocations[0].locationCoordinates);
      if (pos) setMapCenter(pos);
    }
  }, [filteredLocations]);

  useEffect(() => {
    if (!map) return;

    const locationsToFit = filteredLocations?.length
      ? filteredLocations
      : uniqueLocations;

    if (!locationsToFit?.length) return;

    const bounds = new window.google.maps.LatLngBounds();

    locationsToFit.forEach((loc) => {
      const pos = getPosition(loc.locationCoordinates);
      if (pos) {
        bounds.extend(pos);
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);

      // Optional: Prevent too much zoom when only one marker
      if (locationsToFit.length === 1) {
        map.setZoom(14);
      }
    }
  }, [map, filteredLocations, uniqueLocations]);


  // Utility: convert lat/lng object format to {lat, lng}
  const getPosition = (coords) => {
    if (!coords) return null;
    if (coords.lat && coords.lng) return { lat: coords.lat, lng: coords.lng };
    if (coords.latitude && coords.longitude)
      return { lat: coords.latitude, lng: coords.longitude };
    return null;
  };



  // Original vendor toggle handler
  const handleOriginalVendorToggle = () => {
    if (showVendors && vendorViewMode === null) {
      setShowVendors(false);
      setDisplayAllVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange(null);
    } else {
      setDisplayAllVendors(allVendors || []);
      setShowVendors(true);
      setVendorViewMode(null);
      setDisplayApprovedVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange(null);
    }
  };

  const handleShowAllVendors = () => {
    if (showVendors && vendorViewMode === 'all') {
      setShowVendors(false);
      setVendorViewMode(null);
      setDisplayAllVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange(null);
    } else {
      setDisplayAllVendors(allVendors || []);
      setShowVendors(true);
      setVendorViewMode('all');
      setDisplayApprovedVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange('all');
    }
  };

  const handleShowApprovedVendors = () => {
    if (showVendors && vendorViewMode === 'approved') {
      setShowVendors(false);
      setVendorViewMode(null);
      setDisplayApprovedVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange(null);
    } else {
      setDisplayApprovedVendors(approvedVendors || []);
      setShowVendors(true);
      setVendorViewMode('approved');
      setDisplayAllVendors([]);
      if (onVendorViewModeChange) onVendorViewModeChange('approved');
    }
  };

  const displayedVendors = vendorViewMode === 'approved' ? displayApprovedVendors : displayAllVendors;

  if (loadError) return <div>Error loading maps</div>;

  if (!isLoaded)
    return (
      <div className="flex justify-center items-center h-[450px]">
        <CircularProgress />
      </div>
    );

  return (
    <div className="bg-white shadow-md rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="text-left">
          <h2 className="text-lg font-semibold text-gray-800">
            {`Site Mapping: ${filteredUniqueSites?.length} Stores / ${filteredLocations?.length} Jobs${activeProduct ? ` (${activeProduct})` : ""
              }`}
          </h2>
        </div>
      </div>
      {filteredLocations?.length > 0 ? (
        <>
          <div className="mt-4">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={10}
              center={mapCenter} // <--- FIXED center value
              // center={getCenterPosition()}
              onLoad={(mapInstance) => setMap(mapInstance)}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              {/* Main location markers */}
              {filteredLocations?.map((location, index) => {
                const position = getPosition(location.locationCoordinates);
                if (!position) {
                  // console.warn("Invalid coordinates for location:", location);
                  return null;
                }

                return (
                  <Marker
                    key={`location-${index}-${location.details?.phoneNumber ||
                      location.details?.storeOwnerPhoneNumber
                      }`}
                    position={position}
                    onClick={() => setSelectedLocation(location)}
                  />
                );
              })}

              {/* Vendor markers */}
              {showVendors && displayedVendors?.map((vendor, index) => {
                const position = getPosition(vendor.locationCoordinates);
                if (!position) return null;

                return (
                  <Marker
                    key={`vendor-${index}-${vendor._id || vendor.id}`}
                    position={position}
                    icon={{
                      url: vendorViewMode === 'approved'
                        ? "http://maps.google.com/mapfiles/ms/icons/green-dot.png"
                        : "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                    }}
                  />
                );
              })}

              {showIPs &&
                ipList?.map((ip, index) => {
                  const position = getPosition(ip.locationCoordinates);
                  if (!position) return null;

                  return (
                    <Marker
                      key={`ip-${index}`}
                      position={position}
                      icon={{
                        url: "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
                      }}
                    />
                  );
                })}

              {/* InfoWindow for selected location */}
              {selectedLocation && (
                <InfoWindow
                  position={{
                    lat:
                      getPosition(selectedLocation.locationCoordinates).lat +
                      0.0000005,
                    lng: getPosition(selectedLocation.locationCoordinates).lng,
                  }}
                  onCloseClick={() => setSelectedLocation(null)}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      width: 200,
                      borderRadius: 0,
                      padding: 0,
                      overflow: "hidden",
                      border: "none",
                    }}
                  >
                    {/* Header Section */}
                    <div className={styles.locationHeader}>
                      <h4 className={styles.storeName}>
                        {selectedLocation.details?.storeName}
                      </h4>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedLocation(null)}
                        className={styles.closeButton}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>

                    {/* Details */}
                    <div className={styles.detailsContainer}>
                      <div className={styles.detailsLabel}>
                        <p>Owner:</p>
                        <p>Contact:</p>
                        <p>Address:</p>
                      </div>
                      <div className={styles.detailsValue}>
                        <p>
                          {selectedLocation?.details?.OwnerName
                            ? selectedLocation?.details?.OwnerName
                            : selectedLocation?.details?.storeOwnerName}
                        </p>
                        <p>
                          {selectedLocation?.details?.phoneNumber
                            ? selectedLocation?.details?.phoneNumber
                            : selectedLocation?.details?.storeOwnerPhoneNumber}
                        </p>
                        <p className={styles.address}>
                          {`${selectedLocation?.details?.address}, ${selectedLocation?.details?.city}, ${selectedLocation?.details?.state}, ${selectedLocation?.details?.zipCode}`}
                        </p>
                      </div>
                    </div>
                  </Paper>
                </InfoWindow>
              )}
            </GoogleMap>

            {/* Google Maps UI tweaks */}
            <style>
              {`
            .gm-ui-hover-effect {
              display: none !important; /* Hide the default close button */
            }
            .gm-style-iw-d {
              padding: 0 !important; /* Remove padding from InfoWindow */
            }
          `}
            </style>
          </div>

          {showToggles && (
            <>
              {/* Toggles Section - Horizontal Layout */}
              <div className="mt-4 mb-2">
                {/* Row 1: Vendor and IP Toggles (Horizontal) */}
                <div className="flex items-center gap-6 mb-3">
                  {/* Original Vendor Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Vendors</span>
                    <Switch
                      checked={showVendors && vendorViewMode === null}
                      onChange={handleOriginalVendorToggle}
                      color="primary"
                    />
                  </div>

                  {/* IPs Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">IPs</span>
                    <Switch
                      checked={showIPs}
                      onChange={async () => {
                        if (!showIPs) {
                          let totalIPs = await fetchIps();
                          setIPList(totalIPs);
                          setShowIPs(true);
                        } else {
                          setShowIPs(false);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Row 2: New Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleShowAllVendors}
                    variant={showVendors && vendorViewMode === 'all' ? "contained" : "outlined"}
                    color="primary"
                    size="small"
                    sx={{
                      backgroundColor: showVendors && vendorViewMode === 'all' ? '#1976d2' : 'transparent',
                      color: showVendors && vendorViewMode === 'all' ? 'white' : '#1976d2',
                      borderColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: showVendors && vendorViewMode === 'all' ? '#1565c0' : '#e3f2fd',
                      }
                    }}
                  >
                    All Vendors
                  </Button>

                  <Button
                    onClick={handleShowApprovedVendors}
                    variant={showVendors && vendorViewMode === 'approved' ? "contained" : "outlined"}
                    color="success"
                    size="small"
                    sx={{
                      backgroundColor: showVendors && vendorViewMode === 'approved' ? '#2e7d32' : 'transparent',
                      color: showVendors && vendorViewMode === 'approved' ? 'white' : '#2e7d32',
                      borderColor: '#2e7d32',
                      '&:hover': {
                        backgroundColor: showVendors && vendorViewMode === 'approved' ? '#1b5e20' : '#e8f5e9',
                      }
                    }}
                  >
                    Approved Vendors
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center text-gray-500 py-4">
          No locations found for {activeProduct || "this filter"}.
        </div>
      )}
    </div>
  );
};

const fetchIps = async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/installationPartner/view`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (response?.data?.success) {
      return response?.data?.data;
    }
  } catch (error) {
    console.error("Error fetching IPs:", error);
    return [];
  }
};

export default ProductStoreMapping;

